/**
 * Authoring one question, wherever it is being authored.
 *
 * Two places need this and they are not the same place: a workspace's own
 * question bank (`/org/questions`) and the shared section library
 * (`/console/library`). They post to different endpoints, live under different
 * authorities, and are drawn in different skins — but the *question* is the same
 * shape in both, because a library question is copied into a tenant verbatim.
 *
 * So this owns the fields and the answer editor, and each caller owns its own
 * save. Duplicating it instead was the alternative, and duplicating a form whose
 * rules are enforced by a validator elsewhere is how the two drift until one of
 * them starts producing questions the other cannot store.
 */

import { Button, Field, Input, Notice, Select, Textarea } from './ui';

export type QuestionType = 'mc' | 'mc-weighted' | 'numeric' | 'short-text' | 'free-text';

export interface Option {
  id: string;
  text: string;
  weight?: number;
}

/** The fields this editor owns. Callers keep whatever else they need beside it. */
export interface QuestionDraft {
  type: QuestionType;
  prompt: string;
  hint: string | null;
  points: number;
  timeLimitMs: number | null;
  answer: Record<string, unknown>;
  tags: string[];
}

export const TYPES: Array<{ value: QuestionType; label: string; scored: string }> = [
  { value: 'mc', label: 'Multiple choice', scored: 'One option is correct.' },
  {
    value: 'mc-weighted',
    label: 'Weighted choice',
    scored: 'Every option scores something. Points must equal the best one.',
  },
  { value: 'numeric', label: 'Number', scored: 'Compared with optional tolerance.' },
  { value: 'short-text', label: 'Short answer', scored: 'Matched against accepted answers.' },
  { value: 'free-text', label: 'Written answer', scored: 'Never machine-scored — a human reads it.' },
];

export function defaultAnswer(type: QuestionType): Record<string, unknown> {
  switch (type) {
    case 'mc':
      return { options: [{ id: 'a', text: '' }, { id: 'b', text: '' }], correctOptionId: 'a' };
    case 'mc-weighted':
      return { options: [{ id: 'a', text: '', weight: 0 }, { id: 'b', text: '', weight: 0 }] };
    case 'numeric':
      return { answer: 0 };
    case 'short-text':
      return { acceptedAnswers: [] };
    case 'free-text':
      return { scoringGuide: '' };
  }
}

export function blankQuestion(): QuestionDraft {
  return {
    type: 'mc',
    prompt: '',
    hint: null,
    points: 1,
    timeLimitMs: null,
    answer: defaultAnswer('mc'),
    tags: [],
  };
}

export default function QuestionFields({
  draft,
  onChange,
  error,
  field,
}: {
  draft: QuestionDraft;
  onChange: (next: QuestionDraft) => void;
  /** The server's own wording for a refusal, if there was one. */
  error?: string | null;
  /** Which input it was about, so it renders beside that input. */
  field?: string | null;
}) {
  const answer = draft.answer;
  const options = (answer.options as Option[] | undefined) ?? [];

  const setAnswer = (patch: Record<string, unknown>) =>
    onChange({ ...draft, answer: { ...draft.answer, ...patch } });

  const changeType = (type: QuestionType) =>
    // The server narrows the config to its type on write, so leftovers from a
    // switch cannot smuggle a second item type into the row. Clearing here just
    // means the form matches what will be stored.
    onChange({ ...draft, type, answer: defaultAnswer(type) });

  const type = TYPES.find((entry) => entry.value === draft.type);

  return (
    <>
      <div className="grid gap-4 sm:grid-cols-[1fr_10rem_10rem]">
        <Field label="Type" hint={type?.scored} error={field === 'type' ? error : null}>
          <Select
            value={draft.type}
            onChange={(event) => changeType(event.target.value as QuestionType)}
          >
            {TYPES.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </Field>

        <Field
          label="Points"
          required
          error={field === 'points' ? error : null}
          hint={draft.type === 'mc-weighted' ? 'Must equal the best option.' : undefined}
        >
          <Input
            type="number"
            min={0}
            step="0.5"
            value={draft.points}
            onChange={(event) => onChange({ ...draft, points: Number(event.target.value) })}
          />
        </Field>

        <Field label="Time limit" hint="Seconds. Leave blank for the section timer only.">
          <Input
            type="number"
            min={1}
            value={draft.timeLimitMs ? Math.round(draft.timeLimitMs / 1000) : ''}
            onChange={(event) =>
              onChange({
                ...draft,
                timeLimitMs: event.target.value ? Number(event.target.value) * 1000 : null,
              })
            }
          />
        </Field>
      </div>

      <div className="mt-4">
        <Field label="Prompt" required error={field === 'prompt' ? error : null}>
          <Textarea
            value={draft.prompt}
            onChange={(event) => onChange({ ...draft, prompt: event.target.value })}
            rows={4}
            className="prompt-text"
            required
          />
        </Field>
      </div>

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <Field label="Hint" hint="Shown under the prompt, e.g. “Give a number.”">
          <Input
            value={draft.hint ?? ''}
            onChange={(event) => onChange({ ...draft, hint: event.target.value || null })}
          />
        </Field>
        <Field label="Tags" hint="Comma separated. Used for filtering.">
          <Input
            value={draft.tags.join(', ')}
            onChange={(event) =>
              onChange({
                ...draft,
                tags: event.target.value
                  .split(',')
                  .map((value) => value.trim().toLowerCase())
                  .filter(Boolean),
              })
            }
          />
        </Field>
      </div>

      <div className="mt-5 border-t border-veil/[0.06] pt-5">
        {field === 'answer' && error && (
          <div className="mb-4">
            <Notice tone="danger">{error}</Notice>
          </div>
        )}

        {(draft.type === 'mc' || draft.type === 'mc-weighted') && (
          <OptionsEditor
            type={draft.type}
            options={options}
            correctOptionId={answer.correctOptionId as string | undefined}
            onChange={(next, correct) =>
              setAnswer(
                draft.type === 'mc' ? { options: next, correctOptionId: correct } : { options: next },
              )
            }
          />
        )}

        {draft.type === 'numeric' && (
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Answer" required>
              <Input
                type="number"
                step="any"
                value={String(answer.answer ?? '')}
                onChange={(event) => setAnswer({ answer: Number(event.target.value) })}
                required
              />
            </Field>
            <Field label="Tolerance" hint="Optional. The answer is accepted within ± this.">
              <Input
                type="number"
                step="any"
                min={0}
                value={String(answer.tolerance ?? '')}
                onChange={(event) =>
                  setAnswer({ tolerance: event.target.value ? Number(event.target.value) : null })
                }
              />
            </Field>
          </div>
        )}

        {draft.type === 'short-text' && (
          <Field
            label="Accepted answers"
            required
            hint="One per line. Compared casefolded, with punctuation stripped."
          >
            <Textarea
              value={((answer.acceptedAnswers as string[]) ?? []).join('\n')}
              onChange={(event) =>
                setAnswer({
                  acceptedAnswers: event.target.value
                    .split('\n')
                    .map((value) => value.trim())
                    .filter(Boolean),
                })
              }
              rows={3}
              required
            />
          </Field>
        )}

        {draft.type === 'free-text' && (
          <div className="space-y-4">
            <Notice>
              A written answer is never machine-scored. Its points stay reserved for whoever reads
              the report, and the totals say so.
            </Notice>
            <Field label="Scoring guide" required hint="Printed in the report for the reviewer.">
              <Textarea
                value={String(answer.scoringGuide ?? '')}
                onChange={(event) => setAnswer({ scoringGuide: event.target.value })}
                rows={4}
                required
              />
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Word limit" hint="Optional. Shown to the candidate as they type.">
                <Input
                  type="number"
                  min={1}
                  value={String(answer.maxWords ?? '')}
                  onChange={(event) =>
                    setAnswer({ maxWords: event.target.value ? Number(event.target.value) : null })
                  }
                />
              </Field>
              <Field
                label="Keywords"
                hint="Comma separated. Advisory only — they never add to a score."
              >
                <Input
                  value={((answer.keywords as Array<{ term: string }>) ?? [])
                    .map((keyword) => keyword.term)
                    .join(', ')}
                  onChange={(event) =>
                    setAnswer({
                      keywords: event.target.value
                        .split(',')
                        .map((value) => value.trim())
                        .filter(Boolean)
                        .map((term) => ({ term, weight: 0 })),
                    })
                  }
                />
              </Field>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function OptionsEditor({
  type,
  options,
  correctOptionId,
  onChange,
}: {
  type: 'mc' | 'mc-weighted';
  options: Option[];
  correctOptionId: string | undefined;
  onChange: (options: Option[], correctOptionId: string | undefined) => void;
}) {
  const update = (index: number, patch: Partial<Option>) => {
    const next = options.map((option, i) => (i === index ? { ...option, ...patch } : option));
    onChange(next, correctOptionId);
  };

  const add = () => {
    const id = String.fromCharCode(97 + options.length); // a, b, c…
    onChange(
      [...options, { id, text: '', ...(type === 'mc-weighted' ? { weight: 0 } : {}) }],
      correctOptionId,
    );
  };

  const remove = (index: number) => {
    const next = options.filter((_, i) => i !== index);
    onChange(next, next.some((option) => option.id === correctOptionId) ? correctOptionId : undefined);
  };

  const best = type === 'mc-weighted' ? Math.max(0, ...options.map((o) => o.weight ?? 0)) : null;

  return (
    <div>
      <p className="label mb-2">Options</p>
      <ul className="space-y-2">
        {options.map((option, index) => (
          <li key={index} className="flex items-center gap-2">
            {type === 'mc' && (
              <input
                type="radio"
                name="correct"
                checked={correctOptionId === option.id}
                onChange={() => onChange(options, option.id)}
                className="h-4 w-4 shrink-0 accent-accent"
                aria-label={`Option ${option.id} is correct`}
              />
            )}
            <Input
              value={option.id}
              onChange={(event) => update(index, { id: event.target.value })}
              className="w-14 shrink-0 text-center font-mono"
              aria-label="Option id"
            />
            <Input
              value={option.text}
              onChange={(event) => update(index, { text: event.target.value })}
              placeholder="What this option says"
            />
            {type === 'mc-weighted' && (
              <Input
                type="number"
                step="0.5"
                value={option.weight ?? 0}
                onChange={(event) => update(index, { weight: Number(event.target.value) })}
                className="w-20 shrink-0"
                aria-label="Weight"
              />
            )}
            <Button variant="quiet" onClick={() => remove(index)}>
              ×
            </Button>
          </li>
        ))}
      </ul>
      <Button className="mt-2" onClick={add}>
        Add an option
      </Button>
      {type === 'mc' && !correctOptionId && options.length > 0 && (
        <p className="mt-2 text-[12.5px] text-warn">Choose which option is correct.</p>
      )}
      {best !== null && (
        // Said here rather than only on refusal: the rule is unmemorable, and
        // finding out after a save that the points had to match is a worse way
        // to learn it.
        <p className="mt-2 text-[12.5px] text-ink/40">
          Best option scores {best}. The question's points must equal that.
        </p>
      )}
    </div>
  );
}
